/**
 * Re-exports the real, frozen response/pagination contract from
 * `@nexao/contracts` — the single source of truth shared with the
 * backend (`standards/ApiStandards.md`'s own envelope, unchanged).
 * This file never redeclares a shape that already exists there.
 */
export type {
  ApiSuccessResponse,
  ApiErrorResponse,
  ApiResponseBody,
  PaginationQuery,
  PaginationMeta,
  PaginatedResult,
} from '@nexao/contracts';

/**
 * Thrown by `core/api/http-client.ts` for every non-2xx response,
 * normalizing the real `ApiErrorResponse.error` shape into a real JS
 * `Error` subclass so callers can `instanceof` it instead of
 * re-parsing `response.data.error` at every call site.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly statusCode: number;
  readonly details?: unknown;

  constructor(code: string, message: string, statusCode: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
  }

  /** `code` values starting `GEN_VALIDATION` / ending `_VALIDATION_ERROR` — matches every bounded context's own `VALIDATION_ERROR` catalog entry convention (`CLINICAL-ERROR-GUIDE.md` §4). */
  get isValidationError(): boolean {
    return this.statusCode === 422;
  }

  get isAuthError(): boolean {
    return this.statusCode === 401;
  }

  get isForbidden(): boolean {
    return this.statusCode === 403;
  }

  get isNotFound(): boolean {
    return this.statusCode === 404;
  }

  /** 409 Conflict — every bounded context's own optimistic-lock/business-rule-conflict status. */
  get isConflict(): boolean {
    return this.statusCode === 409;
  }
}
